import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

type Props = {
    onChange: (value: string | null) => void;
}

export const SignaturePadAtom = forwardRef<{ clear: () => void }, Props>(({ onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const lastPointRef = useRef<{x: number; y: number;} | null>(null);
    const lastTimeRef = useRef<number | null>(null);
    const prevPointRef = useRef<{x: number; y: number;}| null>(null);
    const [drawing, setDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if(!canvas)
            return;
        canvas.width = 500;
        canvas.height = 200;

        const ctx = canvas.getContext("2d");
        if(!ctx)
            return;
        ctx.lineCap = "round";
        ctx.strokeStyle = "black";
        ctx.lineWidth = 2;
        ctxRef.current = ctx;
    }, []);

    const getPos = (e: any) => {
        if(!canvasRef.current)
            return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        if (e.touches) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top,
            };
        }
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };
    };

    const startDrawing = (e: any) => {
        if(!ctxRef.current)
            return;
        const { x, y } = getPos(e);

        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);

        lastPointRef.current = { x, y };
        prevPointRef.current = { x, y };
        lastTimeRef.current = Date.now();

        setDrawing(true);
    };

    const draw = (e: any) => {
        if (!drawing) 
            return;

        const ctx = ctxRef.current;
        const lastPoint = lastPointRef.current;
        const prevPoint = prevPointRef.current;
        const lastTime = lastTimeRef.current;

        if (!ctx || !lastPoint || !prevPoint || !lastTime) 
            return;

        const { x, y } = getPos(e);
        const now = Date.now();

        const dx = x - lastPoint.x;
        const dy = y - lastPoint.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const time = now - lastTime || 1;

        const velocity = distance / time;

        const minWidth = 1;
        const maxWidth = 4;

        let lineWidth = maxWidth - velocity * 2;
        lineWidth = Math.max(minWidth, Math.min(maxWidth, lineWidth));

        ctx.lineWidth = lineWidth;

        const midPoint = {
            x: (lastPoint.x + x) / 2,
            y: (lastPoint.y + y) / 2,
        };

        ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midPoint.x, midPoint.y);
        ctx.stroke();

        prevPointRef.current = lastPoint;
        lastPointRef.current = { x, y };
        lastTimeRef.current = now;
    };

    const stopDrawing = () => {
        if(!ctxRef.current || !canvasRef.current)
            return;

        setDrawing(false);
        ctxRef.current.closePath();
        
        const dataUrl = canvasRef.current.toDataURL("image/png");
        if (onChange) 
            onChange(dataUrl);
    };

    const clearSign = () => {
        if(!ctxRef.current || !canvasRef.current)
            return;
        const canvas = canvasRef.current;
        ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
        if (onChange) onChange(null);
    };

    useImperativeHandle(ref, () => ({
        clear: clearSign
    }));

    return (
        <>
            <canvas
                ref={canvasRef}
                style={{ 
                    border: "1px dashed #ccc", 
                    touchAction: "none", 
                    cursor: 'url(/media/svg/illustrations/pen.cur), pointer' 
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
            />
        </>
    )
})